import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  CheckCircleIcon,
  AlertIcon,
  InfoIcon,
  XIcon,
} from "../components/ui/Icons";
import { cx, Spinner } from "../components/ui";

const ToastContext = createContext(null);

const TONES = {
  success: {
    Icon: CheckCircleIcon,
    ring: "ring-emerald-200",
    iconCls: "bg-emerald-50 text-emerald-600",
    bar: "bg-emerald-500",
  },
  error: {
    Icon: AlertIcon,
    ring: "ring-red-200",
    iconCls: "bg-red-50 text-red-600",
    bar: "bg-red-500",
  },
  info: {
    Icon: InfoIcon,
    ring: "ring-sky-200",
    iconCls: "bg-sky-50 text-sky-600",
    bar: "bg-sky-500",
  },
  loading: {
    Icon: null,
    ring: "ring-ink-200",
    iconCls: "bg-ink-100 text-ink-500",
    bar: "bg-ink-400",
  },
};

let seq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast) => {
      const id = ++seq;
      const duration = toast.duration ?? (toast.type === "error" ? 7000 : 4000);
      setToasts((prev) => [...prev, { ...toast, id }]);
      if (toast.type !== "loading" && duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  const api = useMemo(
    () => ({
      success: (title, message, opts) =>
        push({ type: "success", title, message, ...opts }),
      error: (title, message, opts) =>
        push({ type: "error", title, message, ...opts }),
      info: (title, message, opts) =>
        push({ type: "info", title, message, ...opts }),
      loading: (title, message) => push({ type: "loading", title, message }),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-5 top-5 z-[100] flex w-full max-w-sm flex-col gap-2.5">
        {toasts.map((t) => {
          const tone = TONES[t.type] || TONES.info;
          const Icon = tone.Icon;
          return (
            <div
              key={t.id}
              className={cx(
                "pointer-events-auto relative flex animate-slide-in-right gap-3 overflow-hidden rounded-xl bg-white p-3.5 pr-10 shadow-lift ring-1",
                tone.ring
              )}
            >
              <span className={cx("absolute inset-y-0 left-0 w-1", tone.bar)} />
              <span
                className={cx(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                  tone.iconCls
                )}
              >
                {Icon ? <Icon size={17} /> : <Spinner size={15} />}
              </span>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-[13.5px] font-semibold leading-snug text-ink-900">
                  {t.title}
                </p>
                {t.message && (
                  <p className="mt-0.5 break-words text-[12.5px] leading-relaxed text-ink-500">
                    {t.message}
                  </p>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="absolute right-2.5 top-2.5 grid h-6 w-6 place-items-center rounded-md text-ink-400 transition hover:bg-ink-100 hover:text-ink-700"
                aria-label="Dismiss"
              >
                <XIcon size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
